import {FormControl, FormControlLabel, Grid, RadioGroup} from '@mui/material';
import clsx from 'clsx';
import React, {Fragment, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import API from '../../../../app/api';
import _ from 'lodash';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import {useDispatch, useSelector} from 'react-redux';
import {additionalData, updatePatientAdditionalData} from '../../../../store/reducers/additionalData';
import {user} from '../../../../store/reducers/user';
import moment from 'moment';
import uuidv4 from 'uuid/v4';
import {addNoteForPatient} from '../../../../store/reducers/notes';
import {useTheme} from '@mui/material/styles';
import {TextFieldDebounce} from '../../../../controls/agyle/textFieldDebounce';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import Notes from '../../../../util/notes';
import Formatter from '../../../../util/formatter';
import InformationBox from '../../../../components/other/informationBox';
import {setSnackbar} from '../../../../store/reducers/globals';
import AgyleDialog from '../../../../components/modal/agyleDialog';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import AgyleDictionaryField from '../../../../components/ui/selector/agyleDictionaryField';
import useOpenState from '../../../../hooks/useOpenState';
import {useStylesUrinalysis} from './useStylesUrinalysis';

const UrinalysisTestNew = (props) => {
    const {t} = useTranslation();
    const classes = useStylesUrinalysis();
    const controller = new AbortController();
    const dispatch = useDispatch();
    const patientAddData = useSelector(additionalData);
    const theme = useTheme();

    const userObject = useSelector(user);
    const tester = Formatter.formatUserFullName(userObject);

    const [list, setList] = useState([]);
    const [selectedTest, setSelectedTest] = useState(null);

    const [formData, setFormData] = useState([]);
    const [isSkipped, setIsSkipped] = useState(false);
    const [additionalInfoData, setAdditionalInfoData] = useState({
        additionalInformation: '',
        bhcg: 'notTested',
    });
    const [isAdditionalInfoOpen, openAdditionalInfo, closeAdditionalInfo] = useOpenState(false, true);

    useEffect(() => {
        API.getAllUrinalysis(controller.signal).then(data => {
            if (data) {
                const filtered = _.filter(data, {active: true});
                const defaultTest = _.find(filtered, {default: true});
                setList(filtered);
                setSelectedTest(defaultTest ? defaultTest : null);
            }
        });
        return () => {
            controller.abort();
        };
    }, []);

    const isValidFirstSection = () => {
        const check = formData && selectedTest && formData.length === selectedTest.tests.length;
        if (!check) {
            dispatch(setSnackbar({type: 'warning', message: 'All category must be selected.'}));
        }
        return check;
    };

    const isValidSecondSection = () => {
        const check = additionalInfoData && !_.isNil(additionalInfoData.bhcg);
        if (!check) {
            dispatch(setSnackbar({type: 'warning', message: 'Please select Urine Beta-HCG.'}));
        }
        return check;
    };

    const saveData = () => {
        if (isValidSecondSection()) {
            const id = uuidv4() + '';
            const data = {
                'urinalysis': createData(id),
            };
            dispatch(updatePatientAdditionalData(props.patient.patient.__id, data));

            const note = createNote();
            dispatch(addNoteForPatient(props.patient.patient.__id, {
                note: note,
                noteType: 'urinalysis',
                patientsInWardsRef: [props.patient.__id],
                referenceId: id,
                episodeId: props.patient?.admission?.consultantEpisodeId || null,
            }, props.patient.__id));
            props.onClose();
        }
    };

    const createData = (id) => {
        let obj = [];
        if (patientAddData.other.urinalysis) {
            obj = [...patientAddData.other.urinalysis];
        }
        const newTest = {
            id: id,
            stripTestName: selectedTest?.description || '',
            stripTestId: selectedTest?.__id || 'none',
            by: tester,
            login: userObject?.userName,
            when: moment().toISOString(),
            skipped: isSkipped,
            deleted: false,
            attendanceId: props.patient?.admission?.__id,

            ...additionalInfoData,
            tests: sort(formData),
        };
        obj.push(newTest);

        return obj;
    };

    const createNote = () => {
        return Notes.getUrinalysisTestNote(selectedTest?.description, formData, additionalInfoData);
    };

    const sort = (arr) => {
        if (!_.isEmpty(arr)) {
            return _.sortBy(arr, ['position']);
        }
        return [];
    };

    const handleSelectValue = (test, category) => {
        const clone = _.cloneDeep(formData);
        const index = _.findIndex(clone, {category: category.name, categoryShortcut: category.shortcut});

        if (index !== -1) {
            const obj = {
                ...clone[index],
                value: test.value,
                valueColour: test.colour,
                valueGrainType: test.grainType,
            };
            _.remove(clone, {category: category.name, categoryShortcut: category.shortcut});
            clone.splice(index, 0, obj);
        } else {
            clone.push({
                category: category.name,
                categoryShortcut: category.shortcut,
                categoryUnit: category.unit,
                position: category.position,
                value: test.value,
                valueColour: test.colour,
                valueGrainType: test.grainType,
            });
        }
        setFormData(clone);
    };

    const isSelected = (test, category) => {
        const find = _.find(formData, {category: category.name, categoryShortcut: category.shortcut});
        return find && find.value === test.value;
    };

    const renderEntryTest = () => {
        if (selectedTest) {
            return (
                <>
                    <InformationBox type={'warning'}>
                        {t('The colours displayed are indicative only!')}
                    </InformationBox>

                    <Paper className={classes.mainTestBox}>
                        {sort(selectedTest.tests).map(test => (
                            <Paper className={classes.testEntry} key={test.name + test.shortcut}>
                                <Grid container direction="row" spacing={2}>
                                    <Grid item xs={3}>
                                        <b>{`${test.name} [${test.shortcut}]`}</b>
                                    </Grid>

                                    <Grid item xs={7}>
                                        <Grid container direction="row">
                                            {sort(test.values).map(element => (
                                                <div
                                                    className={classes.valuesBox}
                                                    key={element.colour + element.value}
                                                >
                                                    <IconButton
                                                        disableRipple
                                                        className={classes.testBar}
                                                        onClick={handleSelectValue.bind(this, element, test)}
                                                        style={{
                                                            backgroundColor: element.colour,
                                                            backgroundImage: element.grainType ? `url(/image/urinalysis/grain_typ_${element.grainType}.png)` : '',
                                                            backgroundSize: 'contain',
                                                            border: `2px solid ${isSelected(element, test) ? 'red' : theme.palette.mode === 'light' ? '#000' : '#fff'}`,
                                                        }}
                                                    />

                                                    <div style={{textAlign: 'center', fontSize: 'small'}}>
                                                        {element.value}
                                                    </div>
                                                </div>
                                            ))}
                                        </Grid>
                                    </Grid>

                                    <Grid item xs={2}>
                                        <span style={{float: 'right'}}>
                                            {test.unit}
                                        </span>
                                    </Grid>
                                </Grid>
                            </Paper>
                        ))}
                    </Paper>
                </>
            );
        }
    };

    const handleOpenAdditional = () => {
        if (isValidFirstSection()) {
            openAdditionalInfo();
        }
    };

    const skipTest = () => {
        setIsSkipped(true);
        openAdditionalInfo();
    };

    return (
        <>
            <AgyleDialog
                open={props.open}
                width="lg"
                fullWidth
                noPadding
                disableSave
                closeButtonOnHeader
                title={t('Submit a new test')}
                onClose={props.onClose}
                buttons={[
                    <AgyleButton
                        variant="contained"
                        key="skip"
                        onClick={skipTest}
                    >
                        {t('Skip test')}
                    </AgyleButton>,
                    <AgyleButton
                        variant="contained"
                        key="submit"
                        onClick={handleOpenAdditional}
                        disabled={!selectedTest}
                    >
                        {t('Submit test')}
                    </AgyleButton>,
                ]}
                titleStyle={classes.backgroundOverride}
                actionStyle={classes.backgroundOverride}
                contentStyle={clsx(classes.backgroundOverride, classes.actionNoDivider)}
            >
                <div style={{padding: 15}}>
                    <Grid container direction="row" spacing={2} style={{minWidth: 600}}>
                        <Grid item xs={12}>
                            <AgyleDictionaryField
                                label={t('Tests')}
                                options={list}
                                value={selectedTest}
                                onChange={(event, newValue) => setSelectedTest(newValue)}
                                getOptionLabel={option => option.description}
                                isOptionEqualToValue={(option, value) => option.__id === value.__id}
                                size="large"
                                noBorder={false}
                                withNewBackground
                            />
                        </Grid>

                        <Grid item xs={12}>
                            {renderEntryTest()}
                        </Grid>
                    </Grid>
                </div>
            </AgyleDialog>

            <AgyleDialog
                open={isAdditionalInfoOpen}
                onClose={closeAdditionalInfo}
                width="sm"
                fullWidth
                closeButtonOnHeader
                noPadding
                disableSave
                title={t('Additional Information')}
                buttons={[
                    <AgyleButton
                        variant="contained"
                        onClick={saveData}
                        key="submit-additional"
                    >
                        {t('Submit')}
                    </AgyleButton>,
                ]}
                titleStyle={classes.backgroundOverride}
                actionStyle={classes.backgroundOverride}
                contentStyle={clsx(classes.backgroundOverride, classes.actionNoDivider)}
            >
                <div style={{padding: 15}}>
                    <Grid item xs={12}>
                        <TextFieldDebounce
                            style={{width: '100%'}}
                            label={t('Additional Information')}
                            multiline
                            agyleRefresh
                            value={additionalInfoData.additionalInformation}
                            variant="outlined"
                            InputLabelProps={{
                                shrink: true,
                            }}
                            rows={3}
                            onChange={event => setAdditionalInfoData(prevState => ({
                                ...prevState,
                                additionalInformation: event.target.value,
                            }))}
                            color="tertiary"
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>

                    <Grid item xs={12} style={{textAlign: 'center', marginTop: 5}}>
                        <FormControl>
                            <FormLabel>
                                <b>{t('Urine BHCG')}</b>
                            </FormLabel>

                            <RadioGroup
                                row
                                value={additionalInfoData.bhcg}
                                onChange={event => setAdditionalInfoData(prevState => ({
                                    ...prevState,
                                    bhcg: event.target.value,
                                }))}
                            >
                                <FormControlLabel value="notTested" control={<Radio/>} label={t('Not Tested')}/>
                                <FormControlLabel value="positive" control={<Radio/>} label={t('Positive')}/>
                                <FormControlLabel value="negative" control={<Radio/>} label={t('Negative')}/>
                            </RadioGroup>
                        </FormControl>
                    </Grid>
                </div>
            </AgyleDialog>
        </>
    );
};

export default UrinalysisTestNew;